import React, { useMemo } from 'react';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { formatToPlural } from '../../../utils';

import type { CopyResult } from './CopyQuestionsDialog';
import type { Campaign } from '../types';

interface CopyResultsListProps {
    copyResults: CopyResult[];
    campaigns: Campaign[];
}

/**
 * Displays the results of a copy questions operation.
 *
 * @param {CopyResultsListProps} props
 * @returns {JSX.Element}
 */
const CopyResultsList: React.FC<CopyResultsListProps> = ({ copyResults, campaigns }) => {
    const getCampaignName = (campaignId: number): string => {
        const campaign = campaigns.find((c) => c.id === campaignId);

        return campaign?.name || 'Unknown project';
    };

    const getProjectTermWithCount = (count: number) => {
        return formatToPlural({ count, singular: 'project', plural: 'projects' });
    };

    const getCopyResultsMessage = (successCount: number, failureCount: number): string => {
        if (successCount > 0 && failureCount === 0) {
            return `Successfully copied questions to ${getProjectTermWithCount(successCount)}.`;
        }

        if (successCount === 0 && failureCount > 0) {
            return `Failed to copy questions to all ${getProjectTermWithCount(failureCount)}.`;
        }

        if (successCount > 0 && failureCount > 0) {
            return `Successfully copied to ${getProjectTermWithCount(successCount)}, but failed for ${failureCount}.`;
        }
        return '';
    };

    const successCount = useMemo(() => copyResults.filter((r) => r.success).length, [copyResults]);
    const failureCount = useMemo(() => copyResults.filter((r) => !r.success).length, [copyResults]);

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {getCopyResultsMessage(successCount, failureCount)}
            </Typography>
            <List>
                {copyResults.map((result) => (
                    <ListItem key={result.campaignId} sx={{ p: 0 }}>
                        <ListItemIcon sx={{ minWidth: 'auto', mr: 0.5 }}>
                            {result.success ? (
                                <CheckCircleIcon fontSize="small" color="success" />
                            ) : (
                                <ErrorIcon fontSize="small" color="error" />
                            )}
                        </ListItemIcon>
                        <ListItemText primary={getCampaignName(result.campaignId)} />
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};

export default CopyResultsList;
